// 这个接口既可以给api server使用，也可以给nuxt 组合式api使用。
import type { CmsInfo, Nav, NavItem, Category, Tags, SinglePage } from '~/types'

/**
 * 根据 navIndex 获取导航树
 * linkUrl 已在 Admin 端生成并存入数据库，这里直接使用
 */
export const getNavItemTreeByNavIndex = (navIndex: string, cmsInfo: CmsInfo): NavItem[] => {
    const nav = cmsInfo.nav.find((item: Nav) => item.navIndex === navIndex)
    if (!nav) {
        return []
    }

    const copyNavItems = JSON.parse(JSON.stringify(nav.navItems))
    // 历史数据 linkUrl 可能为 null，确保不为 null 防止组件调用字符串方法时报错
    copyNavItems.forEach((item: NavItem) => {
        if (!item.linkUrl) {
            item.linkUrl = ''
        }
    })
    const tree = handleTree(copyNavItems as any[], 'id', 'pid')
    return tree
}

/**
 * 根据 url 获取分类
 */
export const getCategoryByUrl = (url: string, cmsInfo: CmsInfo) => {
    return cmsInfo.category.find((item: Category) => item.url === url) ?? null
}

/**
 * 根据 url 获取标签列表
 */
export const getTagsListByUrl = (tagsUrl: string, cmsInfo: CmsInfo) => {
    const tagsList: Tags[] = []
    if (isEmpty(tagsUrl)) {
        return tagsList
    }

    const tagsUrlList = tagsUrl.split('_')
    for (const tagsItemUrl of tagsUrlList) {
        const tags = cmsInfo.tags.find((item: Tags) => item.url === tagsItemUrl)
        if (tags) {
            tagsList.push(tags)
        }
    }
    return tagsList
}


/**
 * 根据 url 获取单页详情
 */
export const getSinglePageByUrl = (url: string, cmsInfo: CmsInfo) => {
    return cmsInfo.singlePage.find((item: SinglePage) => item.url === url) ?? null
}

/**
 * 根据文章详情 URL 获取分类
 */
export const getCategoryDetailByArticleFullUrl = (articleFullUrl: string, cmsInfo: CmsInfo) => {
    const categoryUrl = articleFullUrl.split('/').at(-2)
    const category = cmsInfo.category.find((item: Category) => item.url === categoryUrl)
    if (!category) {
        throw createError({ statusCode: 404, statusMessage: '未找到分类' })
    }
    return category
}


/**
 * 获取指定分类的所有子孙分类 ID
 */
export const getChildCategoryIds = (categoryId: number, cmsInfo: CmsInfo) => {
    const childCategoryIds: number[] = []
    cmsInfo?.category.forEach((item: Category) => {
        if (item.pid === categoryId) {
            childCategoryIds.push(...getChildCategoryIds(item.id, cmsInfo), item.id)
        }
    })
    return childCategoryIds
}

/**
 * 获取标签的所有子孙分类 ID
 */
export const getChildTagsIds = (tagsId: number, cmsInfo: CmsInfo) => {
    const childTagsIds: number[] = []
    cmsInfo?.tags.forEach((item: Tags) => {
        if (item.pid === tagsId) {
            childTagsIds.push(...getChildTagsIds(item.id, cmsInfo), item.id)
        }
    })
    return childTagsIds
}

/**
 * 处理树形结构
 * @param data 数据
 * @param id id
 * @param parentId 父级id
 * @param children 子级
 * @returns 树形结构
 */
export const handleTree = (data: any[], id: string, parentId: string, children?: any) => {
    let config = {
        id: id || 'id',
        parentId: parentId || 'parentId',
        childrenList: children || 'children'
    };
  
    var childrenListMap:any = {};
    var tree = [];
    for (let d of data) {
        let id = d[config.id];
        childrenListMap[id] = d;
    }
  
    for (let d of data) {
        let parentId = d[config.parentId]
        let parentObj = childrenListMap[parentId]
        if (!parentObj) {
            tree.push(d);
        } else {
            parentObj[config.childrenList].push(d)
        }
    }
    return tree;
}

export const isEmpty = (value: any) => {
    return value === null || value === undefined || value === '' || value === 'null' || value === 'undefined' || value === '""'
}


/**
 * 根据category和suffix，生成category的完整url路径
 * 从最底层category递归向上找到根节点，拼接路径
 * @param category 分类
 * @param cmsInfo 
 * @param suffix 后缀（可选）
 * @returns string 完整的url
 */
export const appendCategoryUrl = (category: Category, cmsInfo: CmsInfo, suffix: string): string => {
    if (category.pid === 0 || category.pid === null || category.pid === undefined) {
        return "/" + category.url + suffix
    } else {
        suffix = "/" + category.url + suffix
        const parentCategory: Category = cmsInfo.category.find((item: Category) => item.id === category.pid)!
        return appendCategoryUrl(parentCategory, cmsInfo, suffix)
    }
}


export default{
    getNavItemTreeByNavIndex,
    getCategoryByUrl,
    getTagsListByUrl,
    getSinglePageByUrl,
    getCategoryDetailByArticleFullUrl,
    getChildCategoryIds,
    getChildTagsIds,
    handleTree,
    appendCategoryUrl,
    isEmpty,

}
